import { createAsyncThunk } from "@reduxjs/toolkit";
import { actionModulesApi } from "../../api/actionsModulesApi";
import { AppDispatch, RootState } from "../store";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";
import { ActionModuleInstanceResponse } from "../../types/actionModule/instance/actionModuleInstance";
import { ActionModule } from "../../types/actionModule/actionModule";
import { Dayjs } from "dayjs";

export const fetchActionModules = createAsyncThunk<
  ActionModule[] | null,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "actions/fetchActions",
  async (_, { dispatch }) => {
    const res = await handleRequest<ActionModule[]>(
      dispatch,
      "indirect",
      () => actionModulesApi.getActions()
    );
    return res.success() ? res.result : null;
  }
);

export const editActionThunk = createAsyncThunk<
  number | null,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "action/editAction",
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const actionDataToEdit = state.actions.actionToEdit;

    if (actionDataToEdit && actionDataToEdit.id) {
      const res = await handleRequest<void>(
        dispatch,
        "direct",
        () => actionModulesApi.editAction(actionDataToEdit.id as number, actionDataToEdit as any, true, false),
        i18n.t("requestMessage.actionUpdated")
      );
      if (res.success()) {
        await dispatch(fetchActionModules());
        return actionDataToEdit.id as number;
      }
    }
    return null;
  }
);

export const updateActionModuleOrderThunk = createAsyncThunk<
    number[],
    number[],
    { dispatch: AppDispatch; state: RootState }
>("actionModules/updateActionModuleOrder", async (orderedIds, { dispatch }) => {
    const res = await handleRequest(
        dispatch,
        "direct",
        () => actionModulesApi.updateActionModuleOrder(orderedIds),
        i18n.t("requestMessage.actionModuleOrderUpdated")
    );
    if (!res.success()) {
        throw new Error(res.errorMessage ?? "Failed to update action module order");
    }
    await dispatch(fetchActionModules());
    return orderedIds;
});

export const fetchActionModuleInstancesThunk = createAsyncThunk<
  ActionModuleInstanceResponse[],
  { actionModuleId: number; employeeId: string, startDate: Dayjs, endDate: Dayjs },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "actions/fetchActionModuleInstances",
  async ({ actionModuleId, employeeId, startDate, endDate }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<ActionModuleInstanceResponse[]>(
      dispatch,
      "direct",
      () => actionModulesApi.getActionModuleInstances(
        actionModuleId,
        employeeId,
        startDate,
        endDate
      ),
      i18n.t("requestMessage.actionModuleInstancesFetched")
    );

    if (!res.success()) {
      return rejectWithValue(
        res.errorMessage ?? "Failed to fetch action module instances"
      );
    }

    return res.result ?? [];
  }
);

export const bookTransactionForActionModuleThunk = createAsyncThunk<
  void,
  { actionModuleId: string },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "transactions/bookTransactionForActionModule",
  async ({ actionModuleId }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () => actionModulesApi.bookTransactionForActionModule(actionModuleId),
      i18n.t("requestMessage.transactionBooked")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to book transaction");
    }
    return res.result as void;
  }
);